import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './panels.css';

const SECTION_INFO = [
  {
    code: 'VR',
    name: 'Verbal Reasoning',
    desc: 'Assesses your ability to critically evaluate written information — reading passages and judging whether statements are true, false, or can\'t be determined from the text.',
  },
  {
    code: 'DM',
    name: 'Decision Making',
    desc: 'Tests logical reasoning, problem-solving, and interpreting data presented as text, charts, tables, and diagrams — including probabilistic and statistical reasoning.',
  },
  {
    code: 'QR',
    name: 'Quantitative Reasoning',
    desc: 'Assesses numerical reasoning skills — using arithmetic, algebra, and statistics to solve problems presented in tables, charts, and graphs.',
  },
  {
    code: 'SJT',
    name: 'Situational Judgement',
    desc: 'Measures your ability to understand real-world situations in a healthcare setting and judge how appropriate different responses to them are.',
  },
];

function HomePanel({ user, apiUrl }) {
  const [stats, setStats] = useState(null);
  const [adminStats, setAdminStats] = useState(null);

  useEffect(() => {
    axios.get(`${apiUrl}/platform/stats`).then((r) => setStats(r.data)).catch(() => {});
    if (user.role === 'admin') {
      axios.get(`${apiUrl}/admin/statistics`).then((r) => setAdminStats(r.data)).catch(() => {});
    }
  }, [apiUrl, user.role]);

  const showsExcellence = user.role !== 'student' || user.access_tier === 'ucat_excellence';

  return (
    <div className="panel">
      <h2>Welcome, {user.full_name}!</h2>
      <p className="panel-intro">
        This platform is your UCAT ANZ preparation toolkit from The Studium Education — a full
        question bank, timed mock exams built to the official UCAT ANZ section format, and
        {showsExcellence ? ' the UCAT Excellence Program, a structured 30-week course.' : ' self-guided study material for every section.'}
      </p>

      {stats && (
        <div className="home-stats-row">
          <div className="home-stat">
            <div className="home-stat-value">{stats.total_questions}</div>
            <div className="home-stat-label">Practice Questions</div>
          </div>
          <div className="home-stat">
            <div className="home-stat-value">{stats.total_mini_mocks}</div>
            <div className="home-stat-label">Mini Mock Exams</div>
          </div>
          <div className="home-stat">
            <div className="home-stat-value">{stats.total_full_mocks}</div>
            <div className="home-stat-label">Full Mock Exams</div>
          </div>
        </div>
      )}

      <h3>The Four UCAT ANZ Sections</h3>
      <div className="section-info-grid">
        {SECTION_INFO.map((s) => (
          <div key={s.code} className="section-info-card">
            <div className="section-info-code">{s.code}</div>
            <div className="section-info-name">{s.name}</div>
            <p>{s.desc}</p>
          </div>
        ))}
      </div>

      <h3>Getting Started</h3>
      <ul className="howto-list">
        <li><strong>Question Bank</strong> — practice individual questions by section and topic, with explanations and a difficulty rating on every question.</li>
        <li><strong>Practice Exams</strong> — take Mini Mock (shorter) or Full Mock (real exam length) papers under official UCAT ANZ section timing.</li>
        <li><strong>Self-Guided Study</strong> — reference material for each section, plus general exam tips.</li>
        {showsExcellence && (
          <li><strong>UCAT Excellence Program</strong> — your structured 30-week course, in the sidebar.</li>
        )}
        <li><strong>My Results</strong> — review every past attempt question by question, with personalised feedback on where to focus next.</li>
      </ul>

      {user.role === 'admin' && adminStats && (
        <>
          <h3>Platform Overview</h3>
          <div className="home-stats-row">
            <div className="home-stat">
              <div className="home-stat-value">{adminStats.total_students}</div>
              <div className="home-stat-label">Students</div>
            </div>
            <div className="home-stat">
              <div className="home-stat-value">{adminStats.total_tutors}</div>
              <div className="home-stat-label">Tutors</div>
            </div>
            <div className="home-stat">
              <div className="home-stat-value">{adminStats.total_practice_attempts}</div>
              <div className="home-stat-label">Practice Attempts</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default HomePanel;
